/**
 * Device mockups (docs/19-mockups.md): the catalog of devices a screenshot can
 * be shown in. Sizes are real millimetres (manufacturer data sheets), the
 * screen follows from the diagonal and the native resolution, so a screenshot
 * of the device fills the screen exactly. Drawings and the 3D view live in
 * `draw.ts` and `layout.ts`; nothing here depends on the DOM.
 */

export const DEVICE_CATEGORIES = ["phone", "tablet", "laptop", "desktop", "browser"] as const;
export type DeviceCategory = (typeof DEVICE_CATEGORIES)[number];

export const DEVICE_CATEGORY_LABELS: Record<DeviceCategory, string> = {
  phone: "Smartphones",
  tablet: "Tablets",
  laptop: "Laptops",
  desktop: "Bildschirme",
  browser: "Browser",
};

/** A colour of a device: the visible metal rim (`frame`) and its sides in 3D views. */
export interface DeviceFinish {
  id: string;
  name: string;
  frame: string;
  side: string;
  /** iMac: colour of the chin below the screen. */
  chin?: string;
}

/** Button on the edge of a phone or tablet (portrait, mm from the top or left). */
export interface DeviceButton {
  side: "left" | "right" | "top";
  from: number;
  length: number;
}

export type DeviceCutout =
  | { type: "island"; width: number; height: number; top: number }
  | { type: "notch"; width: number; height: number }
  | { type: "hole"; size: number; top: number };

/** Phones and tablets: one slab with the screen on the front. */
export interface SlateSpec {
  kind: "slate";
  width: number;
  height: number;
  depth: number;
  radius: number;
  /** Visible metal ring around the glass. */
  rim: number;
  /** Screen diagonal in inches. */
  diagonal: number;
  /** Screen corners; default: concentric with the body. */
  screenRadius?: number;
  cutout?: DeviceCutout;
  buttons?: DeviceButton[];
  /** Classic phones: home button below the screen (diameter). */
  homeButton?: number;
  /** Tablets: front camera in the bezel above the screen. */
  camera?: boolean;
}

/** Laptops: a lid with the screen and a base with keyboard and trackpad. */
export interface LaptopSpec {
  kind: "laptop";
  width: number;
  /** Depth of the base (= height of the lid plus a little). */
  depth: number;
  lidHeight: number;
  lidDepth: number;
  baseDepth: number;
  radius: number;
  diagonal: number;
  /** Bezel above the screen (the sides follow from the width). */
  bezelTop: number;
  notch?: { width: number; height: number };
  /** Webcam dot in the top bezel (laptops without notch). */
  camera?: boolean;
}

/** iMac and monitors: a screen on a stand. */
export interface DesktopSpec {
  kind: "desktop";
  width: number;
  height: number;
  depth: number;
  radius: number;
  diagonal: number;
  bezelTop: number;
  /** Colour of the glass border (iMac: white). */
  glass?: string;
  stand: { width: number; height: number; footDepth: number; footWidth: number };
}

/** Browser window: title bar and address bar above the page (any aspect). */
export interface BrowserSpec {
  kind: "browser";
  width: number;
}

export type DeviceSpec = SlateSpec | LaptopSpec | DesktopSpec | BrowserSpec;

export interface DeviceModel {
  id: string;
  name: string;
  category: DeviceCategory;
  /** Release year (sorting, labels). */
  year?: number;
  /** Native resolution in portrait (phones, tablets) or landscape (computers). */
  screen: { width: number; height: number };
  /** Also recognized from these screenshot sizes (other scale factors). */
  alsoFits?: { width: number; height: number }[];
  finishes: DeviceFinish[];
  spec: DeviceSpec;
  /** Words for the search besides the name. */
  tags?: string[];
}

// ---------------------------------------------------------------------------
// Finishes
// ---------------------------------------------------------------------------

const finish = (id: string, name: string, frame: string, side: string, chin?: string) =>
  chin ? { id, name, frame, side, chin } : { id, name, frame, side };

const IPHONE_17_PRO = [
  finish("silber", "Silber", "#dcdddf", "#c3c5c8"),
  finish("cosmic-orange", "Cosmic Orange", "#e2793c", "#c4602a"),
  finish("tiefblau", "Tiefblau", "#344158", "#252f42"),
];
const IPHONE_16_PRO = [
  finish("titan-natur", "Titan Natur", "#bdb7ac", "#a29c91"),
  finish("titan-schwarz", "Titan Schwarz", "#3c3c3e", "#2a2a2c"),
  finish("titan-weiss", "Titan Weiß", "#e4e2dd", "#cbc8c1"),
  finish("titan-wuestensand", "Titan Wüstensand", "#c6ab91", "#ad917a"),
];
const IPHONE_15_PRO = [
  finish("titan-natur", "Titan Natur", "#bab4a9", "#9f998f"),
  finish("titan-blau", "Titan Blau", "#414958", "#2f3642"),
  finish("titan-weiss", "Titan Weiß", "#e3e1dc", "#cac7c0"),
  finish("titan-schwarz", "Titan Schwarz", "#3a3a3c", "#28282a"),
];
const IPHONE_AIR = [
  finish("space-schwarz", "Space Schwarz", "#2c2d30", "#1f2023"),
  finish("wolkenweiss", "Wolkenweiß", "#efeee9", "#d7d5cf"),
  finish("lichtgold", "Lichtgold", "#e8dcc4", "#cfc2a8"),
  finish("himmelblau", "Himmelblau", "#d6e2ec", "#bccad6"),
];
const IPHONE_17 = [
  finish("schwarz", "Schwarz", "#303134", "#222326"),
  finish("lavendel", "Lavendel", "#d7cfe5", "#bdb4cd"),
  finish("salbei", "Salbei", "#c3cfb9", "#a9b69f"),
  finish("nebelblau", "Nebelblau", "#b5c4d4", "#9aaabb"),
  finish("weiss", "Weiß", "#eeeeec", "#d6d6d3"),
];
const IPHONE_16 = [
  finish("schwarz", "Schwarz", "#2f3032", "#212224"),
  finish("weiss", "Weiß", "#eeeeeb", "#d5d5d1"),
  finish("rose", "Rosé", "#f0c6d2", "#d7aab7"),
  finish("blaugruen", "Blaugrün", "#80b3ae", "#669994"),
  finish("ultramarin", "Ultramarin", "#6f7ddc", "#5663c2"),
];
const IPHONE_16E = [
  finish("schwarz", "Schwarz", "#2c2c2e", "#1f1f21"),
  finish("weiss", "Weiß", "#f0f0ee", "#d8d8d5"),
];
const IPHONE_SE = [
  finish("mitternacht", "Mitternacht", "#2d3038", "#20232a"),
  finish("polarstern", "Polarstern", "#ece6dc", "#d3ccc1"),
  finish("rot", "(PRODUCT)RED", "#c8202f", "#a91a27"),
];
const PIXEL = [
  finish("obsidian", "Obsidian", "#2c2d30", "#1e1f22"),
  finish("porcelain", "Porcelain", "#ece6de", "#d3ccc3"),
  finish("moonstone", "Moonstone", "#8e98a3", "#737d88"),
  finish("jade", "Jade", "#c9dccb", "#adc1af"),
];
const GALAXY_ULTRA = [
  finish("titan-silberblau", "Titan Silberblau", "#aab4c0", "#8f99a6"),
  finish("titan-schwarz", "Titan Schwarz", "#3a3b3d", "#28292b"),
  finish("titan-grau", "Titan Grau", "#8b8a87", "#71706d"),
  finish("titan-weisssilber", "Titan Weißsilber", "#dcdcda", "#c2c2c0"),
];
const GALAXY = [
  finish("navy", "Navy", "#2c3549", "#1f2737"),
  finish("eisblau", "Eisblau", "#c3d6e4", "#a8bccb"),
  finish("mint", "Mint", "#c7e0d2", "#abc6b7"),
  finish("silber", "Silberschatten", "#b8babd", "#9ea0a3"),
];
const IPAD_PRO = [
  finish("space-schwarz", "Space Schwarz", "#2c2c2e", "#1f1f21"),
  finish("silber", "Silber", "#dfe0e2", "#c5c6c9"),
];
const IPAD = [
  finish("space-grau", "Space Grau", "#5b5d61", "#46484c"),
  finish("blau", "Blau", "#b7c5d4", "#9dabba"),
  finish("violett", "Violett", "#c8bed8", "#aea4bf"),
  finish("polarstern", "Polarstern", "#e6e1d7", "#ccc7bc"),
];
const GALAXY_TAB = [
  finish("graphit", "Graphit", "#3b3c3f", "#2a2b2e"),
  finish("silber", "Silber", "#c8c9cb", "#adaeb1"),
];
const MACBOOK_PRO = [
  finish("space-schwarz", "Space Schwarz", "#2e2f31", "#232426"),
  finish("silber", "Silber", "#e1e2e4", "#c4c6c9"),
];
const MACBOOK_AIR = [
  finish("mitternacht", "Mitternacht", "#2f3744", "#232a35"),
  finish("polarstern", "Polarstern", "#ede5d8", "#d2c9bb"),
  finish("silber", "Silber", "#e1e2e4", "#c4c6c9"),
  finish("himmelblau", "Himmelblau", "#c8d5e0", "#adbac6"),
];
const LAPTOP = [
  finish("graphit", "Graphit", "#2f3033", "#222326"),
  finish("silber", "Silber", "#c9cbce", "#adb0b3"),
];
const IMAC = [
  finish("blau", "Blau", "#3f73bd", "#2f5c9e", "#a9c6ea"),
  finish("gruen", "Grün", "#3e8a5b", "#2f7048", "#b5dcc1"),
  finish("rose", "Rosé", "#d7566c", "#b84257", "#f2bac4"),
  finish("silber", "Silber", "#c7c9cc", "#aeb0b3", "#e3e4e6"),
  finish("violett", "Violett", "#7a5fb5", "#624a99", "#cfc3ea"),
];
const MONITOR = [
  finish("schwarz", "Schwarz", "#1d1d1f", "#141416"),
  finish("silber", "Silber", "#d6d7d9", "#bcbec1"),
];
const BROWSER = [
  finish("hell", "Hell", "#f4f4f5", "#e4e4e7"),
  finish("dunkel", "Dunkel", "#27272a", "#18181b"),
];

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

const PRO_BUTTONS: DeviceButton[] = [
  { side: "left", from: 27, length: 7 },
  { side: "left", from: 40, length: 11 },
  { side: "left", from: 55, length: 11 },
  { side: "right", from: 44, length: 17 },
];
const IPHONE_BUTTONS: DeviceButton[] = [
  { side: "left", from: 25, length: 5 },
  { side: "left", from: 37, length: 10 },
  { side: "left", from: 51, length: 10 },
  { side: "right", from: 42, length: 16 },
];
const ANDROID_BUTTONS: DeviceButton[] = [
  { side: "right", from: 34, length: 11 },
  { side: "right", from: 52, length: 22 },
];

const ISLAND = { type: "island", width: 20.8, height: 6.1, top: 1.9 } as const;

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

const slate = (spec: Omit<SlateSpec, "kind">): SlateSpec => ({ kind: "slate", ...spec });

export const DEVICE_MODELS: DeviceModel[] = [
  {
    id: "iphone-17-pro",
    name: "iPhone 17 Pro",
    category: "phone",
    year: 2025,
    screen: { width: 1206, height: 2622 },
    finishes: IPHONE_17_PRO,
    spec: slate({
      width: 71.9,
      height: 150,
      depth: 8.75,
      radius: 11.9,
      rim: 0.7,
      diagonal: 6.3,
      cutout: ISLAND,
      buttons: PRO_BUTTONS,
    }),
    tags: ["apple", "ios"],
  },
  {
    id: "iphone-17-pro-max",
    name: "iPhone 17 Pro Max",
    category: "phone",
    year: 2025,
    screen: { width: 1320, height: 2868 },
    finishes: IPHONE_17_PRO,
    spec: slate({
      width: 78,
      height: 163.4,
      depth: 8.75,
      radius: 12.6,
      rim: 0.7,
      diagonal: 6.9,
      cutout: ISLAND,
      buttons: PRO_BUTTONS,
    }),
    tags: ["apple", "ios"],
  },
  {
    id: "iphone-air",
    name: "iPhone Air",
    category: "phone",
    year: 2025,
    screen: { width: 1260, height: 2736 },
    finishes: IPHONE_AIR,
    spec: slate({
      width: 74.7,
      height: 156.2,
      depth: 5.64,
      radius: 12.3,
      rim: 0.6,
      diagonal: 6.5,
      cutout: ISLAND,
      buttons: [
        { side: "left", from: 27, length: 7 },
        { side: "left", from: 40, length: 11 },
        { side: "left", from: 55, length: 11 },
        { side: "right", from: 46, length: 17 },
      ],
    }),
    tags: ["apple", "ios"],
  },
  {
    id: "iphone-17",
    name: "iPhone 17",
    category: "phone",
    year: 2025,
    screen: { width: 1206, height: 2622 },
    finishes: IPHONE_17,
    spec: slate({
      width: 71.5,
      height: 149.6,
      depth: 7.95,
      radius: 11.9,
      rim: 0.7,
      diagonal: 6.3,
      cutout: ISLAND,
      buttons: PRO_BUTTONS,
    }),
    tags: ["apple", "ios"],
  },
  {
    id: "iphone-16-pro",
    name: "iPhone 16 Pro",
    category: "phone",
    year: 2024,
    screen: { width: 1206, height: 2622 },
    finishes: IPHONE_16_PRO,
    spec: slate({
      width: 71.5,
      height: 149.6,
      depth: 8.25,
      radius: 11.9,
      rim: 0.7,
      diagonal: 6.3,
      cutout: ISLAND,
      buttons: PRO_BUTTONS,
    }),
    tags: ["apple", "ios"],
  },
  {
    id: "iphone-16-pro-max",
    name: "iPhone 16 Pro Max",
    category: "phone",
    year: 2024,
    screen: { width: 1320, height: 2868 },
    finishes: IPHONE_16_PRO,
    spec: slate({
      width: 77.6,
      height: 163,
      depth: 8.25,
      radius: 12.6,
      rim: 0.7,
      diagonal: 6.9,
      cutout: ISLAND,
      buttons: PRO_BUTTONS,
    }),
    tags: ["apple", "ios"],
  },
  {
    id: "iphone-16",
    name: "iPhone 16",
    category: "phone",
    year: 2024,
    screen: { width: 1179, height: 2556 },
    finishes: IPHONE_16,
    spec: slate({
      width: 71.6,
      height: 147.6,
      depth: 7.8,
      radius: 11.9,
      rim: 0.8,
      diagonal: 6.1,
      cutout: ISLAND,
      buttons: IPHONE_BUTTONS,
    }),
    tags: ["apple", "ios", "iphone 15"],
  },
  {
    id: "iphone-16e",
    name: "iPhone 16e",
    category: "phone",
    year: 2025,
    screen: { width: 1170, height: 2532 },
    finishes: IPHONE_16E,
    spec: slate({
      width: 71.5,
      height: 146.7,
      depth: 7.8,
      radius: 11.6,
      rim: 0.8,
      diagonal: 6.1,
      cutout: { type: "notch", width: 26.5, height: 5.3 },
      buttons: IPHONE_BUTTONS,
    }),
    tags: ["apple", "ios", "iphone 14", "iphone 13", "notch"],
  },
  {
    id: "iphone-15-pro",
    name: "iPhone 15 Pro",
    category: "phone",
    year: 2023,
    screen: { width: 1179, height: 2556 },
    finishes: IPHONE_15_PRO,
    spec: slate({
      width: 70.6,
      height: 146.6,
      depth: 8.25,
      radius: 11.6,
      rim: 0.7,
      diagonal: 6.1,
      cutout: ISLAND,
      buttons: PRO_BUTTONS,
    }),
    tags: ["apple", "ios"],
  },
  {
    id: "iphone-15-pro-max",
    name: "iPhone 15 Pro Max",
    category: "phone",
    year: 2023,
    screen: { width: 1290, height: 2796 },
    finishes: IPHONE_15_PRO,
    spec: slate({
      width: 76.7,
      height: 159.9,
      depth: 8.25,
      radius: 12.4,
      rim: 0.7,
      diagonal: 6.7,
      cutout: ISLAND,
      buttons: PRO_BUTTONS,
    }),
    tags: ["apple", "ios", "plus"],
  },
  {
    id: "iphone-se",
    name: "iPhone SE",
    category: "phone",
    year: 2022,
    screen: { width: 750, height: 1334 },
    finishes: IPHONE_SE,
    spec: slate({
      width: 67.3,
      height: 138.4,
      depth: 7.3,
      radius: 9.8,
      rim: 0.6,
      diagonal: 4.7,
      screenRadius: 0,
      homeButton: 10.6,
      buttons: [
        { side: "left", from: 18, length: 5 },
        { side: "left", from: 30, length: 9 },
        { side: "left", from: 42, length: 9 },
        { side: "right", from: 30, length: 11 },
      ],
    }),
    tags: ["apple", "ios", "home-button", "iphone 8"],
  },
  {
    id: "pixel-10-pro",
    name: "Google Pixel 10 Pro",
    category: "phone",
    year: 2025,
    screen: { width: 1280, height: 2856 },
    finishes: PIXEL,
    spec: slate({
      width: 72,
      height: 152.8,
      depth: 8.6,
      radius: 11.2,
      rim: 0.8,
      diagonal: 6.3,
      cutout: { type: "hole", size: 3.3, top: 2.6 },
      buttons: ANDROID_BUTTONS,
    }),
    tags: ["android", "google", "pixel 9 pro"],
  },
  {
    id: "pixel-9",
    name: "Google Pixel 9",
    category: "phone",
    year: 2024,
    screen: { width: 1080, height: 2424 },
    finishes: PIXEL,
    spec: slate({
      width: 72,
      height: 152.8,
      depth: 8.5,
      radius: 11.2,
      rim: 0.9,
      diagonal: 6.3,
      cutout: { type: "hole", size: 3.3, top: 2.7 },
      buttons: ANDROID_BUTTONS,
    }),
    tags: ["android", "google"],
  },
  {
    id: "galaxy-s25-ultra",
    name: "Samsung Galaxy S25 Ultra",
    category: "phone",
    year: 2025,
    screen: { width: 1440, height: 3120 },
    alsoFits: [{ width: 1080, height: 2340 }],
    finishes: GALAXY_ULTRA,
    spec: slate({
      width: 77.6,
      height: 162.8,
      depth: 8.2,
      radius: 9.2,
      rim: 0.7,
      diagonal: 6.9,
      cutout: { type: "hole", size: 3.2, top: 2.4 },
      buttons: [
        { side: "right", from: 36, length: 22 },
        { side: "right", from: 64, length: 12 },
      ],
    }),
    tags: ["android", "samsung"],
  },
  {
    id: "galaxy-s25",
    name: "Samsung Galaxy S25",
    category: "phone",
    year: 2025,
    screen: { width: 1080, height: 2340 },
    finishes: GALAXY,
    spec: slate({
      width: 70.5,
      height: 146.9,
      depth: 7.2,
      radius: 10.6,
      rim: 0.8,
      diagonal: 6.2,
      cutout: { type: "hole", size: 3.1, top: 2.3 },
      buttons: [
        { side: "right", from: 32, length: 20 },
        { side: "right", from: 58, length: 11 },
      ],
    }),
    tags: ["android", "samsung"],
  },
  {
    id: "ipad-pro-13",
    name: "iPad Pro 13″",
    category: "tablet",
    year: 2024,
    screen: { width: 2064, height: 2752 },
    finishes: IPAD_PRO,
    spec: slate({
      width: 215.5,
      height: 281.6,
      depth: 5.1,
      radius: 14.5,
      rim: 0.9,
      diagonal: 13,
      camera: true,
      buttons: [
        { side: "top", from: 170, length: 22 },
        { side: "right", from: 22, length: 14 },
        { side: "right", from: 40, length: 14 },
      ],
    }),
    tags: ["apple", "ipados", "tablet"],
  },
  {
    id: "ipad-pro-11",
    name: "iPad Pro 11″",
    category: "tablet",
    year: 2024,
    screen: { width: 1668, height: 2420 },
    finishes: IPAD_PRO,
    spec: slate({
      width: 177.5,
      height: 249.7,
      depth: 5.3,
      radius: 13.5,
      rim: 0.9,
      diagonal: 11.1,
      camera: true,
      buttons: [
        { side: "top", from: 135, length: 20 },
        { side: "right", from: 20, length: 13 },
        { side: "right", from: 37, length: 13 },
      ],
    }),
    tags: ["apple", "ipados", "tablet"],
  },
  {
    id: "ipad-air-11",
    name: "iPad Air 11″",
    category: "tablet",
    year: 2025,
    screen: { width: 1640, height: 2360 },
    finishes: IPAD,
    spec: slate({
      width: 178.5,
      height: 247.6,
      depth: 6.1,
      radius: 14.5,
      rim: 1,
      diagonal: 10.86,
      camera: true,
      buttons: [
        { side: "top", from: 136, length: 20 },
        { side: "right", from: 20, length: 13 },
        { side: "right", from: 37, length: 13 },
      ],
    }),
    tags: ["apple", "ipados", "tablet", "ipad"],
  },
  {
    id: "ipad-mini",
    name: "iPad mini",
    category: "tablet",
    year: 2024,
    screen: { width: 1488, height: 2266 },
    finishes: IPAD,
    spec: slate({
      width: 134.8,
      height: 195.4,
      depth: 6.3,
      radius: 13,
      rim: 1,
      diagonal: 8.3,
      camera: true,
      buttons: [
        { side: "top", from: 98, length: 18 },
        { side: "top", from: 22, length: 11 },
        { side: "top", from: 36, length: 11 },
      ],
    }),
    tags: ["apple", "ipados", "tablet"],
  },
  {
    id: "galaxy-tab-s10",
    name: "Samsung Galaxy Tab S10+",
    category: "tablet",
    year: 2024,
    screen: { width: 1752, height: 2800 },
    finishes: GALAXY_TAB,
    spec: slate({
      width: 185.4,
      height: 285.4,
      depth: 5.6,
      radius: 10.5,
      rim: 0.8,
      diagonal: 12.4,
      camera: true,
      buttons: [
        { side: "right", from: 26, length: 14 },
        { side: "right", from: 44, length: 22 },
      ],
    }),
    tags: ["android", "samsung", "tablet"],
  },
  {
    id: "macbook-pro-14",
    name: "MacBook Pro 14″",
    category: "laptop",
    year: 2024,
    screen: { width: 3024, height: 1964 },
    alsoFits: [{ width: 1512, height: 982 }],
    finishes: MACBOOK_PRO,
    spec: {
      kind: "laptop",
      width: 312.6,
      depth: 221.2,
      lidHeight: 216,
      lidDepth: 5,
      baseDepth: 10.5,
      radius: 10,
      diagonal: 14.2,
      bezelTop: 7,
      notch: { width: 31, height: 6.4 },
    },
    tags: ["apple", "macos", "notebook"],
  },
  {
    id: "macbook-pro-16",
    name: "MacBook Pro 16″",
    category: "laptop",
    year: 2024,
    screen: { width: 3456, height: 2234 },
    alsoFits: [{ width: 1728, height: 1117 }],
    finishes: MACBOOK_PRO,
    spec: {
      kind: "laptop",
      width: 355.7,
      depth: 248.1,
      lidHeight: 243,
      lidDepth: 5.5,
      baseDepth: 11.3,
      radius: 11,
      diagonal: 16.2,
      bezelTop: 7,
      notch: { width: 31, height: 6.4 },
    },
    tags: ["apple", "macos", "notebook"],
  },
  {
    id: "macbook-air-13",
    name: "MacBook Air 13″",
    category: "laptop",
    year: 2025,
    screen: { width: 2560, height: 1664 },
    alsoFits: [{ width: 1280, height: 832 }],
    finishes: MACBOOK_AIR,
    spec: {
      kind: "laptop",
      width: 304.1,
      depth: 215,
      lidHeight: 210,
      lidDepth: 4,
      baseDepth: 7.3,
      radius: 9,
      diagonal: 13.6,
      bezelTop: 8,
      notch: { width: 31, height: 6.4 },
    },
    tags: ["apple", "macos", "notebook"],
  },
  {
    id: "macbook-air-15",
    name: "MacBook Air 15″",
    category: "laptop",
    year: 2025,
    screen: { width: 2880, height: 1864 },
    alsoFits: [{ width: 1440, height: 932 }],
    finishes: MACBOOK_AIR,
    spec: {
      kind: "laptop",
      width: 340.4,
      depth: 237.6,
      lidHeight: 232,
      lidDepth: 4,
      baseDepth: 7.5,
      radius: 9.5,
      diagonal: 15.3,
      bezelTop: 8,
      notch: { width: 31, height: 6.4 },
    },
    tags: ["apple", "macos", "notebook"],
  },
  {
    id: "laptop",
    name: "Laptop 15,6″",
    category: "laptop",
    screen: { width: 1920, height: 1080 },
    alsoFits: [
      { width: 2560, height: 1440 },
      { width: 3840, height: 2160 },
      { width: 1366, height: 768 },
    ],
    finishes: LAPTOP,
    spec: {
      kind: "laptop",
      width: 358,
      depth: 235,
      lidHeight: 230,
      lidDepth: 6,
      baseDepth: 12,
      radius: 7,
      diagonal: 15.6,
      bezelTop: 9,
      camera: true,
    },
    tags: ["windows", "notebook", "linux", "chromebook"],
  },
  {
    id: "imac-24",
    name: "iMac 24″",
    category: "desktop",
    year: 2024,
    screen: { width: 4480, height: 2520 },
    alsoFits: [{ width: 2240, height: 1260 }],
    finishes: IMAC,
    spec: {
      kind: "desktop",
      width: 547,
      height: 400,
      depth: 11.5,
      radius: 9,
      diagonal: 23.5,
      bezelTop: 13.3,
      glass: "#f4f4f5",
      stand: { width: 130, height: 62, footDepth: 147, footWidth: 130 },
    },
    tags: ["apple", "macos", "desktop"],
  },
  {
    id: "monitor-27",
    name: "Monitor 27″",
    category: "desktop",
    screen: { width: 2560, height: 1440 },
    alsoFits: [
      { width: 1920, height: 1080 },
      { width: 3840, height: 2160 },
      { width: 5120, height: 2880 },
    ],
    finishes: MONITOR,
    spec: {
      kind: "desktop",
      width: 614,
      height: 356,
      depth: 22,
      radius: 5,
      diagonal: 27,
      bezelTop: 8.5,
      stand: { width: 70, height: 120, footDepth: 190, footWidth: 240 },
    },
    tags: ["windows", "desktop", "bildschirm", "studio display"],
  },
  {
    id: "browser",
    name: "Browserfenster",
    category: "browser",
    screen: { width: 1440, height: 900 },
    finishes: BROWSER,
    spec: { kind: "browser", width: 1440 },
    tags: ["website", "webseite", "chrome", "safari", "fenster"],
  },
];

const BY_ID = new Map(DEVICE_MODELS.map((model) => [model.id, model]));

export const DEFAULT_DEVICE_ID = "iphone-17-pro";

/** The model of an element; unknown ids (newer catalog) fall back to the default phone. */
export function deviceModel(id: string): DeviceModel {
  return BY_ID.get(id) ?? (BY_ID.get(DEFAULT_DEVICE_ID) as DeviceModel);
}

export function hasDeviceModel(id: string): boolean {
  return BY_ID.has(id);
}

export function deviceFinish(model: DeviceModel, id: string | undefined): DeviceFinish {
  return model.finishes.find((entry) => entry.id === id) ?? (model.finishes[0] as DeviceFinish);
}

/** Phones and tablets can be turned; computers and browser windows stand in landscape. */
export function deviceCanRotate(model: DeviceModel): boolean {
  return model.spec.kind === "slate";
}

/**
 * The picture has exactly the screen's resolution. Phones and tablets match in
 * both orientations; computers only in their own (a 1080 × 1920 video is no
 * laptop screen).
 */
function fitsExactly(model: DeviceModel, width: number, height: number): boolean {
  return [model.screen, ...(model.alsoFits ?? [])].some((size) => {
    if (size.width === width && size.height === height) return true;
    return deviceCanRotate(model) && size.width === height && size.height === width;
  });
}

/**
 * Screenshot size → matching device. Exact resolutions win (newest model
 * first), otherwise the closest aspect ratio of its category. Returns
 * `landscape` when the picture is wider than the device's native screen.
 */
export function suggestDevice(
  width: number | null | undefined,
  height: number | null | undefined,
): { device: string; landscape: boolean } {
  if (!width || !height) return { device: DEFAULT_DEVICE_ID, landscape: false };
  const long = Math.max(width, height);
  const short = Math.min(width, height);
  const wide = width > height;
  const exact = DEVICE_MODELS.filter((model) => fitsExactly(model, width, height)).sort(
    (a, b) => (b.year ?? 0) - (a.year ?? 0),
  );
  const pick = (model: DeviceModel) => {
    const nativeWide = model.screen.width > model.screen.height;
    return {
      device: model.id,
      landscape: deviceCanRotate(model) ? wide !== nativeWide : false,
    };
  };
  if (exact[0]) return pick(exact[0]);
  const ratio = long / short;
  const closest = (category: DeviceCategory) => {
    const aspect = (model: DeviceModel) =>
      Math.max(model.screen.width, model.screen.height) /
      Math.min(model.screen.width, model.screen.height);
    const candidates = DEVICE_MODELS.filter((model) => model.category === category);
    return candidates.reduce((winner, model) =>
      Math.abs(aspect(model) - ratio) < Math.abs(aspect(winner) - ratio) ? model : winner,
    );
  };
  // Nearly square or very long (whole web pages): a browser window takes any shape.
  if (ratio < 1.25 || ratio > 2.35) return { device: "browser", landscape: false };
  if (ratio >= 2) return pick(closest("phone"));
  // Upright 16:9 and taller: phones (screen recordings, reels); squarer: tablets.
  if (!wide) return pick(ratio >= 1.7 ? closest("phone") : closest("tablet"));
  if (ratio < 1.45) return pick(closest("tablet"));
  return { device: ratio < 1.65 ? "macbook-pro-14" : "laptop", landscape: false };
}

/** Catalog search (name, category, tags); empty query: everything. */
export function searchDevices(query: string, category?: DeviceCategory | null): DeviceModel[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return DEVICE_MODELS.filter((model) => {
    if (category && model.category !== category) return false;
    const haystack = [
      model.name,
      model.id,
      DEVICE_CATEGORY_LABELS[model.category],
      ...(model.tags ?? []),
    ]
      .join(" ")
      .toLowerCase();
    return words.every((word) => haystack.includes(word));
  });
}

/**
 * The device a screenshot comes from, when its size is exactly the screen of
 * a named device (else null; generic laptops and monitors never match, their
 * resolutions are too common).
 */
export function exactDevice(
  width: number | null | undefined,
  height: number | null | undefined,
): DeviceModel | null {
  if (!width || !height) return null;
  return (
    DEVICE_MODELS.filter(
      (model) => model.year !== undefined && fitsExactly(model, width, height),
    ).sort((a, b) => (b.year ?? 0) - (a.year ?? 0))[0] ?? null
  );
}
